import React, {useState} from "react";
import {Button, Card, Tabs} from "antd";
import {PlusOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {TICKET_LIST_STATES, TICKET_SCOPES} from "constants/ticket";
import {APPLICATION_TYPES} from "constants/application";
import {TicketList} from "components/tickets/common/TicketList";
import {useTicketOptions} from "components/tickets/common/useTicketOptions";
import {CreateTicketModal} from "components/tickets/create/CreateTicketModal";
import {getApplicationTitle} from "../common/applicationUtils";

const categoryByType = {
    [APPLICATION_TYPES.company]: "onboarding_company",
    [APPLICATION_TYPES.agency]: "onboarding_agency"
};

export const ApplicationTicketsSection = ({type, application, canEdit}) => {
    const {t} = useTranslation(["applications"]);
    const stores = useStore();
    const {roleOptions, roleLabels} = useTicketOptions();
    const [state, setState] = useState(TICKET_LIST_STATES.open);
    const [createVisible, setCreateVisible] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const category = categoryByType[type];

    const [, counts] = useFetch({
        fetchFn: stores.ticketStore.getTicketCounts.bind(null, {
            scope: TICKET_SCOPES.owner,
            ownerId: application._id
        }),
        defaultDataValue: {},
        dependencies: [application._id, refreshKey]
    });

    const refresh = () => setRefreshKey(previous => previous + 1);
    const tabLabel = key =>
        t(`detail.tickets.${key}`, {
            ns: "applications",
            count: counts?.data?.byState?.[key] ?? 0
        });

    return (
        <Card size="small" style={{marginBottom: 16}}>
            <Tabs
                activeKey={state}
                onChange={setState}
                tabBarExtraContent={
                    canEdit && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setCreateVisible(true)}
                        >
                            {t("detail.tickets.create", {ns: "applications"})}
                        </Button>
                    )
                }
            >
                <Tabs.TabPane key={TICKET_LIST_STATES.open} tab={tabLabel("open")} />
                <Tabs.TabPane key={TICKET_LIST_STATES.closed} tab={tabLabel("closed")} />
            </Tabs>
            <TicketList
                key={state}
                scope={TICKET_SCOPES.owner}
                category={category}
                ownerId={application._id}
                state={state}
                compact
                roleOptions={roleOptions}
                roleLabels={roleLabels}
                refreshKey={refreshKey}
                onChanged={refresh}
            />
            <CreateTicketModal
                visible={createVisible}
                category={category}
                ownerId={application._id}
                ownerName={getApplicationTitle(application, type)}
                onCancel={() => setCreateVisible(false)}
                onSuccess={() => {
                    setCreateVisible(false);
                    refresh();
                }}
            />
        </Card>
    );
};
